<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { configure, optOut, optIn } from 'vue-gtag';
import { useHead } from '@vueuse/head';
import Highcharts from 'highcharts';
import 'highcharts/modules/accessibility';
import { router } from '@/routes/index.js';
import { HubspotTracker } from '@/utils/hubspot.js';
import { PendoTracker } from '@/utils/pendo.js';
import NotificationGroup from '@/components/notifications/notification-group.vue';
import ResourceError from '@/components/error.vue';

const props = defineProps({
  rootName: String,
  rootProps: Object,
});

// PAGE TITLE
// Set default page title and template for child component's title
useHead({
  title: 'Traxo',
  titleTemplate: (title) => {
    return title === 'Traxo' ? title : `${title}: Traxo`;
  },
});

// GLOBAL DEFAULTS
Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
  },
});

// USER/TEAM INFO
const store = useStore();
const user = computed(() => store.state.me.user);
const team = computed(() => store.state.me.team);
const teamType = computed(() => store.state.me.teamType);
const userRolesForTeam = computed(() => store.getters['me/userRolesForTeam']);
const isSuperAdmin = computed(() => {
  if (!Array.isArray(userRolesForTeam.value)) {
    return false;
  }

  return userRolesForTeam.value.includes('super-admin');
});

// NOTIFICATIONS
const getNotificationsByType = computed(() => store.getters['notifications/getNotificationsByType']);
const error = computed(() => getNotificationsByType.value('resource')[0]);

// TRACKERS
const route = useRoute();
const Hubspot = ref(null);
const Pendo = ref(null);
const Google = ref(null);
const disableTracking = computed(() => {
  return !!(props.rootProps.disableTracking || route.meta.disableTracking);
});

watch(
  () => route.fullPath,
  () => {
    if (disableTracking.value) {
      destroyTrackers();
      return;
    }

    // Initialize trackers
    if (!Hubspot.value) {
      Hubspot.value = new HubspotTracker(AppConfig.config.hubspot);
    }

    if (!Pendo.value) {
      Pendo.value = new PendoTracker(AppConfig.config.pendo, AppConfig.config.environment);
      Pendo.value.identify(user.value, team.value, teamType.value);
    }

    if (!Google.value && AppConfig?.config?.google?.enabled) {
      configure({
        tagId: AppConfig?.config?.google?.tracking_id,
        initMode: 'auto',
        pageTracker: {
          router,
          exclude: () => disableTracking.value,
        },
      });

      isSuperAdmin.value ? optOut() : optIn();
      Google.value = true;
    }
  }
);

watch(
  () => route.fullPath,
  (to, from) => {
    if (!Hubspot.value) {
      return;
    }

    // when the page loads the from path is '/' and immediately
    // fires a route changes with the actual route. Here we filter
    // out the '/' because the page tracking will get fired from the mounted()
    // event just by instantiating the tracker. If we don't we'll get two page views
    // every time a full page reload happens
    if (from && from !== '/' && to !== from) {
      Hubspot.value.trackPage(to);
    }
  }
);

watch(
  () => team.value.id,
  () => {
    // anytime the team id changes identify the user to pendo
    // our script will manage what needs to be changed when
    if (Pendo.value) {
      Pendo.value.identify(user.value, team.value, teamType.value);
    }

    // Check team id change and track GA depending on
    // if the user is a super admin or not.
    if (Google.value) {
      isSuperAdmin.value ? optOut() : optIn();
    }
  }
);

onUnmounted(() => {
  destroyTrackers();
});

function destroyTrackers() {
  if (Hubspot.value) {
    Hubspot.value.shutDown();
    Hubspot.value = null;
  }

  if (Pendo.value) {
    //TODO: destroy pendo
    Pendo.value = null;
  }

  if (Google.value) {
    optOut();
    Google.value = null;
  }
}
</script>

<template>
  <div class="h-full">
    <!-- Always load notifications -->
    <NotificationGroup />

    <!-- Load the component with rootName -->
    <template v-if="rootName">
      <component
        :is="rootName"
        v-bind="rootProps" />
    </template>

    <!-- Otherwise just load the router-view or error page as normal -->
    <template v-else>
      <ResourceError
        v-if="error"
        :error="error" />
      <RouterView v-else />
    </template>
  </div>
</template>

<style>
/* base tailwind focus-visible styles puts an outline around the
   hubspot iframe all the time Override that. */
#hubspot-messages-iframe-container :focus-visible {
  outline: none;
}
</style>
